// Store
import store from "@/core/services/store";

// API
import {
  ClientClient,
  ApplicationUserClient,
  AuthRequest,
  ImpersonationRequest,
  RefreshRequest,
  ForgotPasswordRequest,
  ResetPasswordRequest
} from "@/core/api/apiClient";
// Models
import { AuthUser } from "@/core/_models/authUser.model";
import { AuthError } from "@/core/_models/authError.model";
import { _Error } from "@/core/_models/_error.model";

const AuthService = {
  /**
   * Logs in a User .
   *
   * @param {string} email The User's email.
   * @param {string} password The User's password.
   * @returns {(Promise<User | any>)} Promise wrapped User or _Error object.
   *
   */
  async login(email: string, password: string): Promise<AuthUser | any> {
    const userClient = new ApplicationUserClient();
    try {
      const request = new AuthRequest({
        username: email,
        password: password
      });
      const response = await userClient.login(request);
      if (response.hasError || response.model == undefined) {
        const authError: AuthError = {
          requirePasswordReset: false,
          isDriver: false
        };

        return authError;
      }
      const user: AuthUser = {
        firstName: response.model.firstName,
        lastName: response.model.lastName,
        userType: response.model.userRole,
        email: response.model.email,
        userId: response.model.id,
        client: undefined,
        clientId: response.model.clientId,
        accessToken: response.model.accessToken,
        refreshToken: response.model.refreshToken
      };
      store.commit("login", user); //set user data

      //drivers are not allowed access to web UI
      if (user.userType === "Driver") {
        store.commit("logout");
        const authError: AuthError = {
          requirePasswordReset: false,
          isDriver: true
        };

        return authError;
      }

      const clientClient = new ClientClient();
      const clientResponse = await clientClient.getClient(response.model.clientId);
      if (clientResponse.hasError || clientResponse.model == undefined) {
        store.commit("logout"); //clear user data
        throw new _Error(
          "401",
          "We couldn't find a user with that email. Please provide a valid email."
        );
      }

      // get user client
      user.client = clientResponse.model;
      return user;
    } catch (error) {
      const authError: AuthError = { requirePasswordReset: false, isDriver: false };
      if ("ErrorCode" in error && error.ErrorCode === 206) {
        authError.requirePasswordReset = true;
      }
      return authError;
    }
  },

  async refreshLogin(): Promise<any> {
    try {
      const user = this.getCurrentUser();
      if (!user || !user.clientId) {
        return false;
      }
      const userClient = new ApplicationUserClient();
      const req = new RefreshRequest({ refreshToken: user.refreshToken, clientId: user.clientId });
      const result = await userClient.refreshToken(req);

      if (result && !result.hasError && result.model?.accessToken) {
        store.commit("refreshLogin", result.model);
      }
      return true;
    } catch {
      return false;
    }
  },

  async impersonateLogin(clientId: string): Promise<AuthUser | any> {
    const userClient = new ApplicationUserClient();
    try {
      const request = new ImpersonationRequest({
        clientId: clientId
      });
      const response = await userClient.impersonate(request);
      if (response.hasError || response.model == undefined) {
        return false;
      }
      const user: AuthUser = {
        firstName: response.model.firstName,
        lastName: response.model.lastName,
        userType: response.model.userRole,
        email: response.model.email,
        userId: response.model.id,
        client: undefined,
        clientId: response.model.clientId,
        accessToken: response.model.accessToken,
        refreshToken: response.model.refreshToken
      };
      store.commit("login", user); //set user data

      const clientClient = new ClientClient();
      const clientResponse = await clientClient.getClient(response.model.clientId);
      if (clientResponse.hasError || clientResponse.model == undefined) {
        store.commit("logout"); //clear user data
        throw new _Error(
          "401",
          "We couldn't find a user with that email. Please provide a valid email."
        );
      }

      // get user client
      user.client = clientResponse.model;
      return user;
    } catch (error) {
      return false;
    }
  },

  /**
   * Logs out a User.
   *
   * @returns {(Promise<any>)} Promise wrapped _Error or general response object.
   *
   */
  async logout(): Promise<any> {
    const userClient = new ApplicationUserClient();
    const user = this.getCurrentUser();
    if (user) {
      await userClient.logout(user.refreshToken);
    }
    const response = {
      code: "ok",
      message: "Successfully signed out!"
    };

    return response;
  },

  /**
   * Resets the password for the provided email
   *
   * @param email
   * @param password
   */
  async resetPassword(email: string, password: string, token: string): Promise<boolean> {
    const userClient = new ApplicationUserClient();
    const req = new ResetPasswordRequest({
      email: email,
      newPassword: password,
      token: token
    });

    const result = await userClient.resetPassword(req);
    return result.model;
  },

  /**
   * Sends a password reset link to the email provided.
   *
   * @param {string} email The email to send the password reset link to.
   * @returns {Promise<boolean>} Promise wrapped _Error or general response.
   *
   */
  async forgotPassword(email: string): Promise<boolean> {
    const userClient = new ApplicationUserClient();

    const req = new ForgotPasswordRequest({
      email: email
    });

    const result = await userClient.forgotPassword(req);

    return result.model;
  },

  /**
   * Gets the current User from Vuex store.
   *
   * @returns {(User | null)} The User or null.
   *
   * @author Nick Brahimir
   */
  getCurrentUser(): AuthUser | null {
    return store.getters.currentUser;
  },

  /**
   * Gets the Main Theme Color for the currentUser in Vuex Store.
   * Returns a red (#F60039) color theme by default.
   *
   * @returns {(string)} The User or null.
   *
   * @author Nick Brahimir
   */
  getMainThemeColor(): string {
    const color = store.getters.currentUser.client.mainThemeColor;
    return color ?? "#f60039";
  }
};

export default AuthService;
